import { useCallback, useEffect, useRef, useState } from "react";
import Quill, { QuillOptionsStatic } from 'quill';
import { debounce } from "underscore";
import { marked } from 'marked';
import classNames from "classnames";
import DOMPurify from 'dompurify';

const Parchment = Quill.import('parchment');

import 'quill/dist/quill.snow.css';
import './quill-editor.scss';

import useClickAwayListener from "../../../utils/use-click-away-listener";
import Icon, { COLORS, ICONS } from "../icon";
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import usePrevious from "../../../utils/use-previous";
import { IRegisterQuillStylesLists } from "./register-quill-styles";

const useCustomSelectLabels = ({
	classPicker,
	defaultLabel,
	toolbarRef,
	editor,
}: {
	classPicker: string;
	defaultLabel: string;
	toolbarRef: React.RefObject<HTMLDivElement>;
	editor: Quill | undefined;
}): void => {
	// update custom select dropdown label on option change
	useEffect(() => {
		const containerEl = toolbarRef?.current?.querySelector?.(classPicker);
		const labelEl = containerEl?.firstElementChild;
		const pickerItems = containerEl?.querySelectorAll('.ql-picker-item');

		const handleClick = (e: Event) => {
			const target = e?.target as HTMLSpanElement | null;
			const val = target?.getAttribute?.('data-value') || '';
			const label = target?.getAttribute?.('data-label') || defaultLabel;
			labelEl?.setAttribute('data-value', val);
			labelEl?.setAttribute('data-label', label);

			if (val) {
				labelEl?.classList.add('ql-active');
			}
		};

		pickerItems?.forEach(el => {
			el.addEventListener('click', handleClick);
		});

		return () => {
			pickerItems?.forEach(el => {
				el.removeEventListener('click', handleClick);
			});
		};
	}, [classPicker, defaultLabel, toolbarRef, editor]); // we need to pass editor in so the toolbar ref properly updates when the component re-renders
};

interface IProps {
	value?: string;
	onChange?: (text: string) => void;
	disabled?: boolean;
	readOnly?: boolean;
	styleLists: IRegisterQuillStylesLists;
	toolbarStyles?: React.CSSProperties;
	disableAnchorTargets?: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
}

const QuillEditor: React.FC<IProps> = ({
	value = '',
	onChange = () => null,
	disabled = false,
	readOnly = false,
	styleLists,
	toolbarStyles = {},
	disableAnchorTargets = false,
	onFocus,
	onBlur,
}) => {

	const {
		FONT_SIZE_ARRAY,
		LINE_HEIGHT_ARRAY,
		COLORS_ARRAY,
		BG_COLORS_ARRAY,
		TEXT_INDENT_ARRAY,
	} = styleLists;

	const moduleLayoutUpdate = useTypedSelector(state => state.CreateEventReducer.moduleLayoutUpdate);
	const loadingEvent = useTypedSelector(state => state.CreateEventReducer.loadingEvent);
	const prevLoadingEvent = usePrevious(loadingEvent);

	const containerRef = useRef<HTMLDivElement>(null);
	const editorRef = useRef<HTMLDivElement>(null);
	const toolbarRef = useRef<HTMLDivElement>(null);
	const lineHeightRef = useRef<HTMLSelectElement>(null);
	const textIndentRef = useRef<HTMLSelectElement>(null);

	const defaultVal = value.replaceAll('&nbsp;', ' '); // for draft js conversion

	const [editor, setEditor] = useState<Quill>();
	const [html, setHtml] = useState(marked.parse(defaultVal));
	const [toolbarOpen, setToolbarOpen] = useState(false);
	const [converted, setConverted] = useState<string>();

	const onClickAway = useCallback(() => {
		setToolbarOpen(false);
		onBlur?.();
	}, [onBlur]);

	useClickAwayListener({
		ref: containerRef,
		onClickAway: () => null,
		onMousedownClickAway: onClickAway, // using mouse down because if we try and highlight text and release the mouse outside of the container we miss the click event
		onEscape: onClickAway,
		open: toolbarOpen,
	});

	// attempt to convert previously created markup from draftjs to html... Unfortunately, it does not properly keep links and formatting.
	useEffect(() => {
		if (defaultVal && !converted && editor) {
			const html = marked.parse(defaultVal);
			setHtml(html);
			setConverted(html);
		}
	}, [defaultVal, converted, editor]);

	const createOptions = useCallback(() => {
		return {
			scrollingContainer: editorRef.current,
			debug: false,
			readOnly,
			modules: {
				toolbar: {
					container: toolbarRef.current,
				},
				keyboard: {
					bindings: {
						tab: {
							key: 9,
							handler: () => {
								onClickAway();
								return true;
							},
						},
					},
				},
			},
			theme: 'snow',
		} as QuillOptionsStatic;
	}, [onClickAway, readOnly]);

	useEffect(() => {
		// anchor target handler
		if (!disableAnchorTargets) {
			const toolbar = editor?.getModule('toolbar');
			toolbar?.addHandler?.('target', () => {

				if (editor) {
					const range = editor.getSelection();
					if (range) {

						// we need to make sure we are properly highlighting the entire anchor content
						// so we need to get the anchor tag, then get the start and end indexes (offset and blot length)
						// and make sure we apply the target attribute at that selection
						// scroll.path is not documented in quill, so need to mark as "any"
						const scrollPath = (editor as any)?.scroll?.path(range.index) ?? [];
						let anchorNode: any;
						// get the anchor tag
						for (let i = 0; i < scrollPath.length; i++) {
							const path = scrollPath[i];
							if (path?.[0]?.domNode?.nodeName === 'A') {
								anchorNode = path?.[0]?.domNode;
								break;
							}
						}

						let _range = range;

						if (anchorNode) {
							// get the start index of the anchor node
							const blot = Parchment.find(anchorNode);
							const offset = blot.offset(editor.scroll);
							// force select text of entire anchor text
							editor.setSelection(offset, blot.length());

							// recalculate the selection based on new forced selection
							const newRange = editor.getSelection();
							if (newRange) {
								_range = newRange;
							}
						}

						const format = editor.getFormat(_range);
						if (format.link) {
							const targetEl = toolbarRef?.current?.querySelector?.('.ql-target');

							if (!format.target) {
								editor.format('target', '_blank');
								targetEl?.classList?.add('ql-active');
							} else {
								const updated = format.target === '_blank' ? '_self' : '_blank';
								// update actual target value on anchor tag
								editor.format('target', updated);

								// update toolbar icon status/color
								if (targetEl) {
									// setTimeout hack to push onto stack so it runs after quill's internal update
									setTimeout(() => {
										if (updated === '_self') {
											targetEl?.classList?.remove('ql-active');
										} else {
											targetEl?.classList?.add('ql-active');
										}
									}, 0);
								}
							}
						} else {
							editor.format('target', '');
						}
					}
				}

			});
		}
	}, [disableAnchorTargets, editor]);

	const spaceMatchers = useCallback((_editor: Quill) => {
		// pasting html into editor creates some unintended spaces and new lines. We remove them here:
		_editor.clipboard.addMatcher("p", (node, delta) => {
			delta?.ops?.forEach?.(op => {
				if (typeof op.insert === 'string' && op.insert?.includes?.('\n\n')) { // remove auto created new line
					op.insert = op.insert?.replace?.('\n\n', '\n');
				}
			});
			const firstOp = delta?.ops?.[0];
			const secondOp = delta?.ops?.[1];
			// if there's a space added before a textindent, remove it
			if (firstOp?.insert === '\t' && secondOp?.attributes?.textindent) {
				firstOp.insert = firstOp.insert.replace("\t", ""); // remove auto created space
			}
			return delta;
		});

		// remove any left over "target" attributes from spans
		_editor.clipboard.addMatcher("span", (node: any, delta: any) => { // ts keeps screamin, so marking as any
			const _delta = {
				...delta,
				ops: delta?.ops?.map((op: any) => {
					if (op?.attributes?.target) {
						const _op = { ...op };
						delete _op.attributes.target;
						return _op;
					}
					return op;
				}) || [],
			};
			return _delta;
		});
	}, []);

	// first initialization of quill
	useEffect(() => {
		let _editor: Quill | undefined;
		if (!editor && editorRef.current && toolbarRef.current) {
			const updatedHtml = marked.parse(defaultVal);

			_editor = new Quill(editorRef.current, createOptions());

			spaceMatchers(_editor);

			if (disabled) _editor.disable();

			const delta = _editor.clipboard.convert({ html: updatedHtml });
			_editor.setContents(delta, 'silent');

			setEditor(_editor);

			_editor.disable(); // prevents auto scrolling because dangerouslyPasteHTML auto focuses the editor and causes a scroll
			_editor.clipboard.dangerouslyPasteHTML(updatedHtml, 'api');
			_editor.enable();
		}
	}, [
		editorRef,
		toolbarRef,
		onClickAway,
		disabled,
		editor,
		createOptions,
		defaultVal,
		spaceMatchers,
	]);

	// oof, this is a tricky effect and it's entire purpose is to "reload" the html when it updates.
	// Its primary purpose is to set the text-align based on the module layout chosen
	const prevLayoutCount = usePrevious(moduleLayoutUpdate);
	useEffect(() => {
		if (
			editorRef.current
			&& containerRef.current
			&& toolbarRef.current
			&& (
				// important: we check if defaultVal !== html so we don't re-render every single instance of the editor rendered to page
				(
					moduleLayoutUpdate
					&& prevLayoutCount !== moduleLayoutUpdate
					&& defaultVal !== html
				)
				// or if we refetch/load the event (this is so we can update if the language changes)
				|| (
					prevLoadingEvent
					&& !loadingEvent
					&& defaultVal !== html
				)
			)
		) {
			const updatedHtml = marked.parse(defaultVal);

			let _editor: Quill | undefined;
			if (editorRef.current && toolbarRef.current) {

				// when the content changes, we need to reinitialize quill or it doesn't properly set new content with updated values
				_editor = new Quill(editorRef.current, createOptions());

				spaceMatchers(_editor);

				if (disabled) _editor.disable();

				const delta = _editor.clipboard.convert({ html: updatedHtml });
				_editor.setContents(delta, 'silent');
				setEditor(_editor);

				_editor.disable(); // prevents auto scrolling because dangerouslyPasteHTML auto focuses the editor and causes a scroll
				_editor.clipboard.dangerouslyPasteHTML(updatedHtml, 'api');
				_editor.enable();
			}

			setHtml(updatedHtml);
		}
	}, [
		moduleLayoutUpdate,
		defaultVal,
		prevLayoutCount,
		html,
		onClickAway,
		loadingEvent,
		prevLoadingEvent,
		disabled,
		createOptions,
		spaceMatchers,
	]);

	// show toolbar when focusing editor (for tabbing)
	useEffect(() => {
		const handleFocus = () => {
			if (editor && !disabled) {
				setToolbarOpen(true);
				onFocus?.();
			}
		};

		if (editor) {
			editor.root.addEventListener('focus', handleFocus);
		}

		return () => {
			if (editor) {
				editor.root.removeEventListener('focus', handleFocus);
			}
		};
	}, [editor, disabled, onFocus]);

	const debounced = useRef(debounce((value: string, cb: (value: string) => void) => {
		cb(value);
	}, 300)).current;

	// handle text change
	useEffect(() => {
		if (editor) {
			// always use "editor" and not "editorRef.current" as the ref will "cache" old versions of onChange
			editor.on('text-change', () => {
				const innerHtml = editor?.root?.innerHTML || '';
				if (innerHtml) {
					setHtml(innerHtml || '');
					debounced(innerHtml, (val) => {
						const clean = DOMPurify.sanitize(val, { ADD_ATTR: ['target'] });
						onChange(clean);
					});
				}
			});
		}
	}, [editor, debounced, onChange]);

	// quill does not update custom selected values in the toolbar, so we need to manually do it
	const handleCustomSelectChange = useCallback((format, ref, arr: any[], style: string, defaultLabel = '') => {
		if (ref.current) {
			const el = ref.current.querySelector('.ql-picker-label') as HTMLSpanElement;
			if (el) {
				if (format[style]) {
					const matchedItem = arr.find(item => item.value === format[style]);
					el.dataset.value = matchedItem?.value || '';
					el.dataset.label = matchedItem?.label || defaultLabel;
					if (matchedItem) {
						el.classList.add('ql-active');
					} else {
						el.classList.remove('ql-active');
					}
				} else {
					el.dataset.value = '';
					el.dataset.label = defaultLabel;
					el.classList.remove('ql-active');
				}
			}
		}
	}, []);

	// update toolbar selections on cursor selection change
	useEffect(() => {
		if (editor) {
			// set the value of the current selected text indent and line height so we can properly show the
			// selected value in the toolbar
			editor.on('selection-change', (range) => {
				if (range) {
					const format = editor.getFormat(range);
					if (lineHeightRef.current) {
						handleCustomSelectChange(format, lineHeightRef, LINE_HEIGHT_ARRAY, 'lineheight', 'Default');
					}
					if (textIndentRef.current) {
						handleCustomSelectChange(format, textIndentRef, TEXT_INDENT_ARRAY, 'textindent', 'None');
					}

					// toggle active state on target icon
					if (format?.target && !disableAnchorTargets) {
						const targetEl = toolbarRef?.current?.querySelector?.('.ql-target');
						if (targetEl) {
							if (format.target === '_self') {
								targetEl?.classList?.remove('ql-active');
							} else {
								targetEl?.classList?.add('ql-active');
							}
						}
					}
				}
			});
		}
	}, [LINE_HEIGHT_ARRAY, TEXT_INDENT_ARRAY, disableAnchorTargets, editor, handleCustomSelectChange]);

	useCustomSelectLabels({ toolbarRef, classPicker: '.ql-text-indent', defaultLabel: 'None', editor });
	useCustomSelectLabels({ toolbarRef, classPicker: '.ql-line-height', defaultLabel: 'Default', editor });

	// updates the link popover placeholder text
	useEffect(() => {
		const toolbar = editor?.getModule('toolbar');
		if (!toolbar) return;

		const tooltip = toolbar?.quill?.theme?.tooltip;
		const input = tooltip?.root?.querySelector('input[data-link]');

		if (input?.dataset) {
			input.dataset.link = 'https://brandlive.com';
		}

	}, [editor]); // we need to pass editor in so the toolbar ref properly updates when the component re-renders

	const handleUndo = () => {
		// I think Quill has outdated types and while history is technically on the object, it's missing from the types,
		// therefore casting as "any"
		return (editor as any)?.history?.undo?.();
	};

	const handleRedo = () => {
		// I think Quill has outdated types and while history is technically on the object, it's missing from the types,
		// therefore casting as "any"
		return (editor as any)?.history?.redo?.();
	};

	if (loadingEvent) return null;
	return (
		<div
			className={classNames("quill-wrap", { active: toolbarOpen })}
			ref={containerRef}
			onClick={() => !disabled && setToolbarOpen(true)}
			onMouseDown={() => !disabled && setToolbarOpen(true)}
		>
			<div
				ref={toolbarRef}
				style={toolbarStyles}
				className={classNames('ql-toolbar', 'ql-snow', { active: toolbarOpen })}
			>
				<span className="ql-formats extra-space">
					<select defaultValue="" className="ql-header">
						<option value="1">Heading 1</option>
						<option value="2">Heading 2</option>
						<option value="3">Heading 3</option>
						<option value="4">Heading 4</option>
						<option value="5">Heading 5</option>
						<option value="6">Heading 6</option>
						<option value="">Unstyled</option>
					</select>
					{/* <select className="ql-font">
										<option>Sans Serif</option>
										<option value="serif">Serif</option>
										<option value="monospace">Monospace</option>
									</select> */}
				</span>

				<span className="ql-formats extra-space">
					<select defaultValue="" className="ql-size">
						<option value="disabled" disabled>Font Size</option>
						<option value="">Default</option>
						{FONT_SIZE_ARRAY.map(val => (
							<option key={val.value} value={val.value}>{val.label}</option>
						))}
					</select>
				</span>

				<span className="ql-formats" ref={lineHeightRef}>
					<select className="ql-line-height lineheight">
						<option value="disabled" disabled>Line height</option>
						<option value="">Default</option>
						{LINE_HEIGHT_ARRAY.map(val => (
							<option key={val.value} value={val.value}>{val.label}</option>
						))}
					</select>
				</span>

				<span className="ql-formats" ref={textIndentRef}>
					<select className="ql-text-indent textindent">
						<option value="disabled" disabled>Text indent</option>
						<option value="">None</option>
						{TEXT_INDENT_ARRAY.map(val => (
							<option key={val.value} value={val.value}>{val.label}</option>
						))}
					</select>
				</span>

				<span className="ql-formats">
					<button className="ql-bold"></button>
				</span>

				<span className="ql-formats">
					<button className="ql-italic"></button>
				</span>

				<span className="ql-formats">
					<button className="ql-underline"></button>
				</span>

				<span className="ql-formats">
					<button className="ql-strike"></button>
				</span>

				<span className="ql-formats" style={{ position: 'relative' }}>
					<select className="ql-color">
						{COLORS_ARRAY.map(val => (
							<option key={val} value={val}>{val}</option>
						))}
					</select>
				</span>

				<span className="ql-formats">
					<select className="ql-background">
						{BG_COLORS_ARRAY.map(val => (
							<option key={val} value={val}>{val}</option>
						))}
					</select>
				</span>

				<span className="ql-formats">
					<button className="ql-list" value="ordered"></button>
				</span>

				<span className="ql-formats">
					<button className="ql-list" value="bullet"></button>
				</span>

				<span className="ql-formats">
					<select className="ql-align">
						<option value=""></option>
						<option value="center"></option>
						<option value="right"></option>
						<option value="justify"></option>
					</select>
				</span>

				<span className="ql-formats">
					<button className="ql-blockquote"></button>
				</span>

				<span className={classNames("ql-formats links-container", { 'disable-anchor-tag': disableAnchorTargets })}>
					<button className="ql-link"></button>
					<button className="ql-target"><Icon name={ICONS.NEW_WINDOW} color={COLORS.WHITE} size={20} /></button>
				</span>

				<span className="ql-formats">
					<button className="ql-code-block"></button>
				</span>
				{/* do not use image, it will freeze because it will try to save a very large buffer into the DB using this particular set up */}
				{/* I'm only keeping it around to show that we can actually use images if we want to */}
				{/* <button className="ql-image"></button> */}
				{/* <button className="ql-video"></button> */}

				<span className="ql-formats">
					<button className="ql-script" value="sub"></button>
				</span>

				<span className="ql-formats">
					<button className="ql-script" value="super"></button>
				</span>

				<span className="ql-formats">
					<button onClick={handleUndo}><Icon name={ICONS.UNDO} color={COLORS.WHITE} size={20} /></button>
				</span>

				<span className="ql-formats">
					<button onClick={handleRedo}><Icon name={ICONS.REDO} color={COLORS.WHITE} size={20} /></button>
				</span>

				<span className="ql-formats">
					<button className="ql-clean"></button>
				</span>

			</div>

			<div ref={editorRef}>
				<div dangerouslySetInnerHTML={{ __html: html }}></div>
			</div>
		</div>
	);
};

export default QuillEditor;
